import React from "react";
import {
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Divider,
} from "@mui/material";
import ReviewStyles from "./ReviewStyles";

interface ReviewProps {
  formData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role: string;
    driverType: string;
    relationshipToInsured: string;
    filingOnBehalf: string;
    dateOfLoss: string;
    timeOfLoss: string;
    reportedDate: string;
    reportedTime: string;
    incidentType: string;
    description: string;
    IncidentLocation: string;
    weatherConditions: string;
    policeReportFiled: boolean;
    policeReportNumber: string;
    authoritiesContacted: string;
    hasWitness: boolean;
    witnessName: string;
    witnessPhone: string;
    witnessEmail: string;
    witnessRelationship: string;
    vehicleYear: string;
    vehicleMake: string;
    vehicleModel: string;
    vin: string;
    //licensePlate: string;
    vehicleLocation: string;
    vehicleDamageDescription: string;
    injuryType: "bodily-injury" | "property-damage" | null;
    injuryDescription: string;
    medicalProviderInfo: string;
    treatmentStatus: string;
    propertyType: string;
    propertyOwnerName: string;
    propertyOwnerContact: string;
    propertyDamageDescription: string;
    propertyDamagePhotos: File[];
    vehicleDamagePhotos: File[];
    confirmTrueDetails: boolean;
    policyNumber: string;
    policyType: string;
    insuredName: string;
    insuredContact: string;
    effectiveDate: string;
    expiryDate: string;
  };
  selectedClaimType: "first-party" | "third-party" | null;
  selectedDamageType: "vehicle" | "injury" | "both" | null;
  uploadedFiles: File[];
  reviewTab: number;
  onReviewTabChange: (newValue: number) => void;
  onFormChange: (field: string, value: any) => void;
}

const Review: React.FC<ReviewProps> = ({
  formData,
  selectedClaimType,
  selectedDamageType,
  uploadedFiles,
  reviewTab,
  onReviewTabChange,
  onFormChange,
}) => {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString();
  };

  const formatTime = (time: string) => {
    return time;
  };

  const renderField = (label: string, value: any) => {
    if (
      !value ||
      value === "Not specified" ||
      (Array.isArray(value) && value.length === 0)
    ) {
      return null;
    }
    return (
      <Box sx={ReviewStyles.flexCol}>
        <Typography
          variant="subtitle2"
          id={`Review-${label}`}
          data-test-id={`test-${label}`}
          aria-label={`${label}`}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          id={`Review-${label}`}
          data-test-id={`test-${label}`}
          aria-label={`${label}`}
        >
          {Array.isArray(value) ? `${value.length} item(s)` : value}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={ReviewStyles.container}>
      <Typography variant="h6" gutterBottom sx={ReviewStyles.title}>
        Review & Submit
      </Typography>
      <Box sx={ReviewStyles.tabBox}>
        <Tabs
          value={reviewTab}
          onChange={(e, newValue) => onReviewTabChange(newValue)}
        >
          <Tab
            label="Personal Info"
            id="Personal Info"
            data-test-id="test-Personal Info"
            aria-label="Select Personal Info"
          />
          <Tab
            label="Incident Details"
            id="Incident Details"
            data-test-id="test-Incident-Details"
            aria-label="Select Incident Details"
          />
          <Tab
            label="Damage Details"
            id="Damage-Details"
            data-test-id="test-Damage-Details"
            aria-label="Select Damage Details"
          />
          <Tab
            label="Evidence"
            id="Evidence"
            data-test-id="test-Evidence"
            aria-label="Select Evidence"
          />
        </Tabs>
      </Box>

      {reviewTab === 0 && (
        <Grid container spacing={2}>
          {selectedClaimType && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                gutterBottom
                id="Claim-Type"
                data-test-id="test-Claim Type"
                aria-label="Claim Type"
              >
                Claim Type
              </Typography>
              <Typography variant="body1" gutterBottom>
                {selectedClaimType === "first-party"
                  ? "First Party Claim"
                  : "Third Party Claim"}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Divider sx={ReviewStyles.sectionDivider} />
          </Grid>

          {/* Personal Information */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              gutterBottom
              id="Personal-Information"
              data-test-id="test-Personal-Information"
              aria-label="Personal Information"
            >
              Personal Information
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={ReviewStyles.flexWrapBox}>
              {renderField(
                "Full Name",
                `${formData.firstName} ${formData.lastName}`.trim()
              )}
              {renderField("Email", formData.email)}
              {renderField("Phone", formData.phone)}
              {renderField("Role", formData.role)}
              {renderField("Driver Type", formData.driverType)}
              {renderField(
                "Relationship to Insured",
                formData.relationshipToInsured
              )}
              {renderField("Filing on behalf of", formData.filingOnBehalf)}
            </Box>
          </Grid>

          {/* Policy Information */}
          {(formData.policyNumber ||
            formData.policyType ||
            formData.insuredName ||
            formData.insuredContact ||
            formData.effectiveDate ||
            formData.expiryDate) && (
            <>
              <Grid item xs={12}>
                <Divider sx={ReviewStyles.sectionDivider} />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  id="Policy Information"
                  data-test-id="test-Policy-Information"
                  aria-label="Policy Information"
                >
                  Policy Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={ReviewStyles.flexWrapBox}>
                  {renderField("Policy Number", formData.policyNumber)}
                  {renderField("Policy Type", formData.policyType)}
                  {renderField("Insured Name", formData.insuredName)}
                  {renderField("Insured Contact", formData.insuredContact)}
                  {renderField(
                    "Effective Date",
                    formData.effectiveDate
                      ? formatDate(formData.effectiveDate)
                      : null
                  )}
                  {renderField(
                    "Expiry Date",
                    formData.expiryDate ? formatDate(formData.expiryDate) : null
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {reviewTab === 1 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              gutterBottom
              id="Incident Details"
              data-test-id="test-Incident-Details"
              aria-label="Incident Details"
            >
              Incident Details
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={ReviewStyles.flexWrapBox}>
              {renderField(
                "Date of Loss",
                formData.dateOfLoss ? formatDate(formData.dateOfLoss) : null
              )}
              {renderField("Time of Loss", formData.timeOfLoss)}
              {renderField(
                "Reported Date",
                formData.reportedDate ? formatDate(formData.reportedDate) : null
              )}
              {renderField("Reported Time", formData.reportedTime)}
              {renderField("Incident Type", formData.incidentType)}
              {renderField("Weather Conditions", formData.weatherConditions)}
              {renderField("Incident Location", formData.IncidentLocation)}
              {renderField("Description", formData.description)}
            </Box>
          </Grid>

          {/* Police Report Information */}
          {(formData.policeReportFiled ||
            formData.policeReportNumber ||
            formData.authoritiesContacted) && (
            <>
              <Grid item xs={12}>
                <Divider sx={ReviewStyles.sectionDivider} />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  id="Police Report Information"
                  data-test-id="test-Police-Report-Information"
                  aria-label="Police Report Information"
                >
                  Police Report Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={ReviewStyles.flexWrapBox}>
                  {renderField(
                    "Police Report Filed",
                    formData.policeReportFiled ? "Yes" : null
                  )}
                  {renderField(
                    "Police Report Number",
                    formData.policeReportNumber
                  )}
                  {renderField(
                    "Authorities Contacted",
                    formData.authoritiesContacted
                  )}
                </Box>
              </Grid>
            </>
          )}

          {/* Witness Information */}
          {(formData.hasWitness ||
            formData.witnessName ||
            formData.witnessPhone ||
            formData.witnessEmail ||
            formData.witnessRelationship) && (
            <>
              <Grid item xs={12}>
                <Divider sx={ReviewStyles.sectionDivider} />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  id="Witness Information"
                  data-test-id="test-Witness-Information"
                  aria-label="Witness Information"
                >
                  Witness Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={ReviewStyles.flexWrapBox}>
                  {renderField(
                    "Has Witness",
                    formData.hasWitness ? "Yes" : null
                  )}
                  {renderField("Witness Name", formData.witnessName)}
                  {renderField("Witness Phone", formData.witnessPhone)}
                  {renderField("Witness Email", formData.witnessEmail)}
                  {renderField(
                    "Witness Relationship",
                    formData.witnessRelationship
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {reviewTab === 2 && (
        <Grid container spacing={2}>
          {selectedDamageType && selectedClaimType === "third-party" && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle1"
                id="Damage-Type"
                data-test-id="test-Damage-Type"
                aria-label="Damage-Type"
              >
                Damage Type
              </Typography>
              <Typography variant="body2">
                {selectedDamageType === "vehicle"
                  ? "Vehicle Damage"
                  : selectedDamageType === "injury"
                  ? "Injury/Property"
                  : selectedDamageType === "both"
                  ? "Both"
                  : null}
              </Typography>
            </Grid>
          )}

          {/* Vehicle Information */}
          {(formData.vehicleYear ||
            formData.vehicleMake ||
            formData.vehicleModel ||
            formData.vin ||
            formData.vehicleLocation ||
            formData.vehicleDamageDescription) && (
            <>
            {selectedDamageType && selectedClaimType === "third-party" && (
              <Grid item xs={12}>
                <Divider sx={ReviewStyles.sectionDivider} />
              </Grid>
            )}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  id="Vehicle-Information"
                  data-test-id="test-Vehicle-Information"
                  aria-label="Vehicle Information"
                >
                  Vehicle Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={ReviewStyles.flexWrapBox}>
                  {renderField("Year", formData.vehicleYear)}
                  {renderField("Make", formData.vehicleMake)}
                  {renderField("Model", formData.vehicleModel)}
                  {renderField("VIN", formData.vin)}
                  {renderField("Vehicle Location", formData.vehicleLocation)}
                  {renderField(
                    "Vehicle Damage Description",
                    formData.vehicleDamageDescription
                  )}
                </Box>
              </Grid>
            </>
          )}

          {/* Injury/Property Information */}
          {((formData.injuryType &&
            formData.injuryType !== "property-damage") ||
            formData.injuryDescription ||
            formData.medicalProviderInfo ||
            formData.treatmentStatus ||
            formData.propertyType ||
            formData.propertyOwnerName ||
            formData.propertyOwnerContact ||
            formData.propertyDamageDescription) && (
            <>
              <Grid item xs={12}>
                <Divider sx={ReviewStyles.sectionDivider} />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  id="Injury/Property-Information"
                  data-test-id="test-Injury/Property-Information"
                  aria-label="Injury/Property Information"
                >
                  Injury/Property Information
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={ReviewStyles.flexWrapBox}>
                  {renderField(
                    "Injury Type",
                    formData.injuryType === "bodily-injury"
                      ? "Bodily Injury"
                      : formData.injuryType === "property-damage"
                      ? "Property Damage"
                      : null
                  )}
                  {renderField(
                    "Injury Description",
                    formData.injuryDescription
                  )}
                  {renderField(
                    "Medical Provider Info",
                    formData.medicalProviderInfo
                  )}
                  {renderField("Treatment Status", formData.treatmentStatus)}
                  {renderField("Property Type", formData.propertyType)}
                  {renderField(
                    "Property Owner Name",
                    formData.propertyOwnerName
                  )}
                  {renderField(
                    "Property Owner Contact",
                    formData.propertyOwnerContact
                  )}
                  {renderField(
                    "Property Damage Description",
                    formData.propertyDamageDescription
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      )}

      {reviewTab === 3 && (
        <Grid container spacing={2}>
          {formData.vehicleDamagePhotos.length > 0 ||
          formData.propertyDamagePhotos.length > 0 ||
          uploadedFiles.length > 0 ? (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1">Uploaded Files</Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={ReviewStyles.flexWrapBox}>
                  {renderField(
                    "Vehicle Damage Photos",
                    formData.vehicleDamagePhotos
                  )}
                  {renderField(
                    "Property Damage Photos",
                    formData.propertyDamagePhotos
                  )}
                  {renderField("Uploaded Files", uploadedFiles)}
                </Box>
              </Grid>
            </>
          ) : (
            <Grid item xs={12}>
              <Typography
                variant="body2"
                color="text.secondary"
                id="No-evidence-uploaded"
                data-test-id="test-No-evidence-uploaded"
                aria-label="No evidence uploaded"
              >
                No evidence uploaded.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      <Box sx={ReviewStyles.confirmBox}>
        <FormControlLabel
          control={
            <Checkbox
              id="confirm-checkbox"
              data-test-id="test-confirm-checkbox"
              aria-label="Select-confirm-checkbox"
              checked={formData.confirmTrueDetails}
              onChange={(e) =>
                onFormChange("confirmTrueDetails", e.target.checked)
              }
            />
          }
          label={
            <Typography
              sx={ReviewStyles.confirmLabel}
              id="confirm-tab"
              data-test-id="test-confirm-tab"
              aria-label="confirm-tab"
            >
              I confirm that all information provided is true and accurate to
              the best of my knowledge.
            </Typography>
          }
        />
      </Box>
    </Box>
  );
};

export default Review;
